import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { onIdTokenChanged, signInWithCustomToken, signOut, type User } from 'firebase/auth/web-extension';
import AccountShell from './AccountShell';
// Already in the main bundle (App.tsx renders it statically) — no new chunk weight.
import SignInScreen from '../SignInScreen/SignInScreen';
import billingApi, { assertSafeRedirectUrl } from '../../services/billingApi';
import entitlementApi, { type Entitlement } from '../../services/entitlementApi';
import { trackMonetizationEvent, type MonetizationEventName } from '../../services/telemetry';
import { trackUpgradeSignin, trackWebCheckoutStarted } from '../../services/analytics';
import { consumeWebRelayResult, hasPendingWebRelayResult } from '../../services/safariRelayAuth';
import { auth } from '../../config/firebaseConfig';
import {
  type KnownOffer,
  type OfferState,
  type PlanKey,
  type UpgradeAttribution,
  type UpgradeParams,
  buildUpgradeQuery,
  clearUpgradeIntent,
  parseAttribution,
  parseUpgradeParams,
  promoToSend,
  readUpgradeIntent,
  resolveOffer,
  saveUpgradeIntent,
} from './upgradeOffers';
import './UpgradePage.css';

export { parseUpgradeParams };
export type { UpgradeParams };

// ── Icons ─────────────────────────────────────────────────────────────────

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true">
    <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <rect x="2" y="6" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4.5 6V4.5a2.5 2.5 0 0 1 5 0V6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const RefreshIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M1.5 7A5.5 5.5 0 0 1 12.3 4.5M12.5 7A5.5 5.5 0 0 1 1.7 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M10.5 2.5l2 2-2 2M3.5 11.5l-2-2 2-2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SupportIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M7 9V7.5c1 0 2-0.7 2-1.75S9 4 7 4s-2 0.9-2 1.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="7" cy="10.5" r="0.6" fill="currentColor" />
  </svg>
);

const ErrorIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ── Feature data ──────────────────────────────────────────────────────────

// i18n keys (under account:upgrade.freeFeatures / account:upgrade.proFeatures).
// Values are resolved via t() at render so they stay translatable.
const FREE_FEATURE_KEYS = [
  'clockDate',
  'verseOfDay',
  'dailyBackground',
  'qiblaCompass',
  'basicTasks',
];

// prayerTimes intentionally absent: free for everyone since 2026-08-03
// (see src/hooks/useFeatureAccess.ts) — never advertise it as Pro.
const PRO_FEATURE_KEYS = [
  'weatherForecast',
  'verseFavorites',
  'notesAi',
  'priorityTasks',
  'verseReflection',
];

// ── Plans ─────────────────────────────────────────────────────────────────

// Plan keys carry no display copy; labels/prices come from i18n
// (account:upgrade.plans.<key>.*). yearly carries a badge.
const PLAN_KEYS: PlanKey[] = ['monthly', 'yearly'];
const PLANS_WITH_BADGE: Record<PlanKey, boolean> = { monthly: false, yearly: true };

// ── State model (marketing-hq/closer/upgrade-signin-spec.md §2) ─────────────

type AuthState = 'resolving' | 'signedOut' | 'signedIn';
type PageState = 'plan' | 'signingIn' | 'checkingOut' | 'offerNotApplied' | 'alreadyPro' | 'error';
/** A CTA press (or a relay return) that is waiting for auth to resolve. */
type QueuedAction = 'press' | 'resume';

const AUTH_RESOLVE_TIMEOUT_MS = 4000;
/** A relay return also exchanges the token with our backend; the CTA is already
 *  busy ("Opening secure checkout…"), so it gets longer before giving up. */
const RELAY_RESOLVE_TIMEOUT_MS = 15000;
const ENTITLEMENT_WAIT_MS = 3000;

interface SessionUser {
  uid: string;
  email: string | null;
  /** False for an unverified email/password account: Firebase holds a live
   *  session, but the backend rejects its token, so it is not "signed in". */
  eligible: boolean;
}

const isEligible = (u: User): boolean => {
  if (u.emailVerified) return true;
  const ids = u.providerData.map((p) => p.providerId);
  return !ids.includes('password') || ids.some((id) => id !== 'password');
};

const withTimeout = <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(fallback), ms);
    promise.then(
      (v) => { window.clearTimeout(timer); resolve(v); },
      () => { window.clearTimeout(timer); resolve(fallback); },
    );
  });

interface Arrival {
  link: UpgradeParams;
  attribution: UpgradeAttribution;
  ct: string | null;
  /** A Google/Apple relay round trip just landed back on this page. */
  relayReturn: boolean;
  /** …and the visitor had already pressed the CTA: continue straight to checkout. */
  resumeCheckout: boolean;
}

/** Read once, before the ct effect or the relay rewrites the URL. Pure reads only
 *  (it runs in a state initializer, which StrictMode calls twice). */
const readArrival = (): Arrival => {
  const search = window.location.search;
  if (hasPendingWebRelayResult()) {
    // The relay returns to the bare /upgrade URL; the choices rode in sessionStorage.
    const intent = readUpgradeIntent();
    if (intent) {
      return {
        link: { plan: intent.plan, promo: intent.promo },
        attribution: { source: intent.source, utmCampaign: intent.utmCampaign },
        ct: null,
        relayReturn: true,
        resumeCheckout: true,
      };
    }
    return { link: parseUpgradeParams(search), attribution: parseAttribution(search), ct: null, relayReturn: true, resumeCheckout: false };
  }
  return {
    link: parseUpgradeParams(search),
    attribution: parseAttribution(search),
    ct: new URLSearchParams(search).get('ct'),
    relayReturn: false,
    resumeCheckout: false,
  };
};

const stripCtFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete('ct');
  const clean = params.toString() ? `?${params.toString()}` : window.location.pathname;
  window.history.replaceState(null, '', clean);
};

// ── Component ─────────────────────────────────────────────────────────────

const UpgradePage: React.FC = () => {
  const { t, i18n } = useTranslation(['account']);
  const [arrival] = React.useState(readArrival);
  const { link, attribution } = arrival;
  // Offer expiry is judged once per page load; Stripe enforces the real cutoff.
  const [now] = React.useState(() => Date.now());

  const [plan, setPlan] = React.useState<PlanKey>(link.plan ?? 'yearly');
  const [pageState, setPageState] = React.useState<PageState>(arrival.resumeCheckout ? 'checkingOut' : 'plan');
  const [queued, setQueued] = React.useState<QueuedAction | null>(arrival.resumeCheckout ? 'resume' : null);
  const [resolved, setResolved] = React.useState(false);
  // A ct or relay sign-in is still running. The resolve timeout may already
  // have shown the page, but no other sign-in or checkout may start until it
  // settles, or its late result could swap the account underneath (presses queue).
  const [handoffPending, setHandoffPending] = React.useState(arrival.relayReturn || arrival.ct !== null);
  const [sessionUser, setSessionUser] = React.useState<SessionUser | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // A session created without the requested promo: held until the visitor has
  // read why, then reused on the next press (never a second session).
  const [heldUrl, setHeldUrl] = React.useState<string | null>(null);
  // The server refused a known offer: never advertise that price again this load.
  const [offerRejected, setOfferRejected] = React.useState(false);
  const [entitlement, setEntitlement] = React.useState<Entitlement | null>(null);
  // State drives the CTA label; the ref feeds analytics, which reads it in the
  // same tick as setSignedInHere (completeSignIn → startCheckout).
  const [signedInHere, setSignedInHere] = React.useState(arrival.resumeCheckout);
  const signedInHereRef = React.useRef(arrival.resumeCheckout);

  const ctaRef = React.useRef<HTMLButtonElement>(null);
  const alreadyProTitleRef = React.useRef<HTMLHeadingElement>(null);
  const trustedUidRef = React.useRef<string | null>(null);
  // Listener updates are ignored until the initial resolution finishes, so a stale
  // session on this origin can never be shown while a ct/relay sign-in is in flight.
  const listenRef = React.useRef(false);
  const resolveStartedRef = React.useRef(false);
  const signInCompletedRef = React.useRef(false);
  const restoreFocusRef = React.useRef(false);
  const pageStateRef = React.useRef(pageState);
  const entitlementRef = React.useRef<{ uid: string; promise: Promise<Entitlement | null> } | null>(null);
  // Bumped by switchAccount: a checkout started for the previous account
  // must not redirect once its awaits return.
  const checkoutGenRef = React.useRef(0);

  const snapshot = (u: User | null): SessionUser | null =>
    u ? { uid: u.uid, email: u.email, eligible: u.uid === trustedUidRef.current || isEligible(u) } : null;

  const authState: AuthState = !resolved ? 'resolving' : sessionUser?.eligible ? 'signedIn' : 'signedOut';

  // ── Offer resolution (display only; Stripe decides the charge) ──────────
  const offerState = resolveOffer(link, plan, now);
  const notApplied = offerRejected && offerState.kind === 'live';
  const effectiveOffer: OfferState = notApplied ? { kind: 'none' } : offerState;
  const liveOffer: KnownOffer | null = effectiveOffer.kind === 'live' ? effectiveOffer.offer : null;
  // The yearly option and the hero line describe the yearly offer whichever plan
  // is selected, so toggling plans never shifts the hero.
  const yearlyOfferState = resolveOffer(link, 'yearly', now);
  const yearlyOffer: KnownOffer | null =
    yearlyOfferState.kind === 'live' && !offerRejected ? yearlyOfferState.offer : null;

  const yearlyPrice = t('account:upgrade.plans.yearly.price');
  const offerName = (o: KnownOffer) => t(`account:upgrade.offer.${o.i18n}.name`);
  const formatOfferDate = (iso: string): string => {
    const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', timeZone: 'America/New_York' };
    try {
      return new Intl.DateTimeFormat(i18n.language, opts).format(new Date(iso));
    } catch {
      return new Intl.DateTimeFormat('en', opts).format(new Date(iso));
    }
  };

  // ── Analytics (spec §8) ─────────────────────────────────────────────────
  const track = (event: MonetizationEventName, extra?: Record<string, unknown>) => {
    void trackMonetizationEvent(event, attribution.source, {
      surface: 'upgrade_page',
      utm_campaign: attribution.utmCampaign,
      plan,
      promo: link.promo,
      ...extra,
    });
  };

  // ── Auth ────────────────────────────────────────────────────────────────
  React.useEffect(() => { pageStateRef.current = pageState; }, [pageState]);

  React.useEffect(() => onIdTokenChanged(auth, (u) => {
    // snapshot reads only refs, so subscribing once is correct.
    if (listenRef.current) setSessionUser(snapshot(u));
  }), []);

  // Resolve auth from Firebase directly — never from AuthContext, whose
  // `loading` can wait on an extension that isn't there (spec §0, second trap).
  React.useEffect(() => {
    // Runs exactly once, StrictMode included: the ct token and the relay result
    // are single-use, so a second pass would sign in with a spent token.
    if (resolveStartedRef.current) return;
    resolveStartedRef.current = true;

    const timer = window.setTimeout(
      () => setResolved(true),
      arrival.relayReturn ? RELAY_RESOLVE_TIMEOUT_MS : AUTH_RESOLVE_TIMEOUT_MS,
    );
    const finish = () => {
      window.clearTimeout(timer);
      listenRef.current = true;
      setSessionUser(snapshot(auth.currentUser));
      setResolved(true);
      setHandoffPending(false);
    };

    void (async () => {
      if (arrival.relayReturn) {
        const result = await consumeWebRelayResult();
        clearUpgradeIntent();
        if (result?.user && auth.currentUser) {
          trustedUidRef.current = auth.currentUser.uid;
          track('upgrade_signin_completed');
          trackUpgradeSignin('completed');
        } else {
          setError(t('account:upgrade.errors.signInFailed'));
        }
        finish();
        return;
      }

      // A stale intent from an abandoned relay attempt must never auto-resume later.
      clearUpgradeIntent();

      if (arrival.ct) {
        // Always consume the token, even if a session already exists: a stale or
        // switched-account session on account.rhemn.com would otherwise check out
        // for the WRONG account. Remove it from the URL so it isn't bookmarked.
        stripCtFromUrl();
        try {
          const cred = await signInWithCustomToken(auth, arrival.ct);
          trustedUidRef.current = cred.user.uid;
        } catch {
          // Whoever is signed in here is not provably the app's account.
          await signOut(auth).catch(() => undefined);
          setError(t('account:upgrade.errors.appSessionExpired'));
        }
        finish();
        return;
      }

      try {
        await auth.authStateReady();
      } catch {
        /* resolve as whatever currentUser is */
      }
      finish();
    })();
    // Mount-only by design (see above); t/track are read from the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEntitlement = React.useCallback((uid: string): Promise<Entitlement | null> => {
    if (entitlementRef.current?.uid !== uid) {
      const request = { uid, promise: entitlementApi.getMyEntitlement().catch(() => null) };
      entitlementRef.current = request;
      // Share only the PENDING lookup: once settled it is released, so a later
      // checkout (e.g. after a purchase and a back/forward restore) asks again.
      void request.promise.then(() => {
        if (entitlementRef.current === request) entitlementRef.current = null;
      });
    }
    return entitlementRef.current.promise;
  }, []);

  // As soon as someone is signed in, find out in the background whether they
  // already have Pro — before any press, so they never pay twice.
  const signedInUid = authState === 'signedIn' && sessionUser ? sessionUser.uid : null;
  React.useEffect(() => {
    if (!signedInUid) return;
    let current = true;
    void loadEntitlement(signedInUid).then((ent) => {
      if (!current || !ent?.isPro) return;
      setEntitlement(ent);
      // checkingOut awaits the same promise and switches state itself.
      setPageState((s) => (s === 'checkingOut' ? s : 'alreadyPro'));
    });
    return () => { current = false; };
  }, [signedInUid, loadEntitlement]);

  // ── Checkout ────────────────────────────────────────────────────────────
  const redirectToCheckout = (url: string, promoApplied: boolean) => {
    setPageState('checkingOut');
    track('checkout_started', { promo_applied: promoApplied, signed_in_here: signedInHereRef.current });
    trackWebCheckoutStarted();
    window.location.href = url;
  };

  const startCheckout = async () => {
    setError(null);
    if (heldUrl) {
      redirectToCheckout(heldUrl, false);
      return;
    }
    setPageState('checkingOut');
    const gen = checkoutGenRef.current;
    const stale = () => gen !== checkoutGenRef.current;

    const uid = auth.currentUser?.uid;
    if (uid) {
      // Fail open after 3s: Stripe stays the source of truth.
      const ent = await withTimeout(loadEntitlement(uid), ENTITLEMENT_WAIT_MS, null);
      if (stale()) return;
      if (ent?.isPro) {
        setEntitlement(ent);
        setPageState('alreadyPro');
        return;
      }
    }

    const promoCode = promoToSend(effectiveOffer);
    try {
      const origin = window.location.origin;
      // Backing out of Stripe lands on /upgrade/cancel, whose "Back to upgrade"
      // link forwards this query, so the offer and attribution survive.
      const back = buildUpgradeQuery({
        plan,
        promo: effectiveOffer.kind === 'none' ? null : link.promo,
        ...attribution,
      });
      const session = await billingApi.createCheckoutSession({
        successUrl: `${origin}/upgrade/success`,
        cancelUrl: `${origin}/upgrade/cancel${back}`,
        idempotencyKey: `upgrade-${Date.now()}`,
        plan,
        ...(promoCode ? { promoCode } : {}),
        source: attribution.source,
        ...(attribution.utmCampaign ? { utmCampaign: attribution.utmCampaign } : {}),
      });
      if (stale()) return;
      assertSafeRedirectUrl(session.url, 'Checkout session');
      if (promoCode && !session.promoApplied) {
        // Never send anyone to Stripe at a price other than the one on screen
        // without a second, explicit press.
        setHeldUrl(session.url);
        if (effectiveOffer.kind === 'live') {
          setOfferRejected(true);
          setPageState('offerNotApplied');
        } else {
          setPageState('plan');
        }
        return;
      }
      redirectToCheckout(session.url, Boolean(promoCode));
    } catch (e) {
      if (stale()) return;
      setError(e instanceof Error ? e.message : t('account:upgrade.errors.checkoutFailed'));
      setPageState('error');
    }
  };

  // ── Sign-in layer ───────────────────────────────────────────────────────
  const openSignIn = () => {
    signInCompletedRef.current = false;
    // Survives the Google/Apple relay round trip (same-tab navigation).
    saveUpgradeIntent({ plan, promo: offerState.kind === 'none' ? null : link.promo, ...attribution });
    setPageState('signingIn');
    track('upgrade_signin_shown');
    trackUpgradeSignin('shown');
  };

  const handleDismiss = () => {
    clearUpgradeIntent();
    restoreFocusRef.current = true;
    setPageState('plan');
  };

  /** Reached from SignInScreen's onSignedIn AND from Firebase (the email
   *  verification path promotes the user without calling onSignedIn). */
  const completeSignIn = () => {
    if (signInCompletedRef.current || pageStateRef.current !== 'signingIn') return;
    signInCompletedRef.current = true;
    clearUpgradeIntent();
    signedInHereRef.current = true;
    setSignedInHere(true);
    track('upgrade_signin_completed');
    trackUpgradeSignin('completed');
    // They already pressed the CTA once: go straight on, no second press.
    void startCheckout();
  };

  React.useEffect(() => {
    if (pageState === 'signingIn' && authState === 'signedIn') completeSignIn();
    // completeSignIn is guarded by refs; re-running on its identity is pointless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState, authState]);

  const layerOpen = pageState === 'signingIn';
  React.useEffect(() => {
    if (!layerOpen) return;
    const html = document.documentElement;
    const appRoot = document.getElementById('root');
    html.classList.add('up-scroll-locked');
    // The layer is portalled out of #root, so this makes everything behind it
    // unreachable by Tab and by assistive tech — a real modal, no focus-trap lib.
    if (appRoot) appRoot.inert = true;
    const raf = window.requestAnimationFrame(() => document.getElementById('signin-email')?.focus());
    return () => {
      window.cancelAnimationFrame(raf);
      html.classList.remove('up-scroll-locked');
      if (appRoot) appRoot.inert = false;
    };
  }, [layerOpen]);

  React.useEffect(() => {
    if (pageState === 'plan' && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      ctaRef.current?.focus();
    }
    // The CTA unmounts when alreadyPro replaces the card; don't drop focus on <body>.
    if (pageState === 'alreadyPro' && document.activeElement === document.body) {
      alreadyProTitleRef.current?.focus();
    }
  }, [pageState]);

  // Restored from the back/forward cache (Back from Google/Apple or Stripe):
  // in-flight UI is stale. A half-finished OAuth has left AuthContext busy, so
  // reload; a busy CTA just goes back to the plan.
  React.useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (pageStateRef.current === 'signingIn') window.location.reload();
      else if (pageStateRef.current === 'checkingOut') setPageState('plan');
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // ── CTA ─────────────────────────────────────────────────────────────────
  const proceed = (action: QueuedAction) => {
    if (action === 'resume') {
      // Only the account the relay just signed in may auto-continue: if the relay
      // failed, an older session on this origin must never be checked out unasked.
      if (authState === 'signedIn' && sessionUser?.uid === trustedUidRef.current) void startCheckout();
      else setPageState('plan');
      return;
    }
    if (authState === 'signedIn') void startCheckout();
    else openSignIn();
  };

  // A press during `resolving` waits (busy) for auth, then takes the right branch.
  // No sign-in surface can mount before auth resolves, so a ct arrival never flashes one.
  React.useEffect(() => {
    if (!queued || authState === 'resolving' || handoffPending) return;
    setQueued(null);
    proceed(queued);
    // Runs with this render's values; proceed has no other inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued, authState, handoffPending]);

  const busy = pageState === 'checkingOut' || queued !== null;

  const onCtaPress = () => {
    if (busy) return;
    if (authState === 'resolving' || handoffPending) setQueued('press');
    else proceed('press');
  };

  const selectPlan = (key: PlanKey) => {
    setPlan(key);
    // That session was created for the previous plan.
    setHeldUrl(null);
    setPageState((s) => (s === 'offerNotApplied' || s === 'error' ? 'plan' : s));
  };

  const switchAccount = () => {
    if (busy) return;
    checkoutGenRef.current += 1;
    trustedUidRef.current = null;
    entitlementRef.current = null;
    setEntitlement(null);
    setHeldUrl(null);
    signedInHereRef.current = false;
    setSignedInHere(false);
    setError(null);
    setPageState('plan');
    void signOut(auth).catch(() => undefined);
  };

  const ctaLabel = busy
    ? t(signedInHere ? 'account:upgrade.ctaOpening' : 'account:upgrade.ctaLoading')
    : notApplied
      ? t('account:upgrade.offer.ctaRegular', { price: yearlyPrice })
      : t('account:upgrade.cta');

  const accountLabel = sessionUser?.email || sessionUser?.uid || '';
  const appleManaged = entitlement?.source === 'apple';

  // ── Render ──────────────────────────────────────────────────────────────
  const renderPricingNotes = () => {
    if (liveOffer) {
      return (
        <>
          <p className="up-promo-note" role="status">
            {t('account:upgrade.offer.codeLine', { name: offerName(liveOffer), code: link.promo })}
          </p>
          <p className="up-offer-then">{t('account:upgrade.offer.then', { price: yearlyPrice })}</p>
          {liveOffer.endsAt && (
            <p className="up-offer-ends">
              {t('account:upgrade.offer.endsOn', { date: formatOfferDate(liveOffer.endsAt) })}
            </p>
          )}
        </>
      );
    }
    if (notApplied) {
      return (
        <p className="up-promo-note up-promo-note--muted" role="status">
          {t('account:upgrade.offer.notApplied', { code: link.promo, price: yearlyPrice })}
        </p>
      );
    }
    if (offerState.kind === 'ended' && offerState.offer.endsAt) {
      return (
        <p className="up-promo-note up-promo-note--muted">
          {t('account:upgrade.offer.ended', { date: formatOfferDate(offerState.offer.endsAt), price: yearlyPrice })}
        </p>
      );
    }
    if (offerState.kind === 'otherPlan' && !offerRejected) {
      return (
        <p className="up-promo-note up-promo-note--muted">
          {t('account:upgrade.offer.yearlyOnly', { name: offerName(offerState.offer) })}
        </p>
      );
    }
    if (effectiveOffer.kind === 'unknown' && heldUrl) {
      return (
        <p className="up-promo-note" role="status">
          {t('account:upgrade.promoManual', { code: effectiveOffer.code })}
        </p>
      );
    }
    return null;
  };

  const renderAccountLine = () => {
    if (authState === 'signedOut') return t('account:upgrade.signedOutHint');
    if (authState === 'signedIn') {
      return (
        <>
          {t('account:upgrade.signingUpAs', { account: accountLabel })}{' '}
          <button type="button" className="up-text-btn" onClick={switchAccount} disabled={busy}>
            {t('account:upgrade.switchAccount')}
          </button>
        </>
      );
    }
    return null; // resolving: the 18px line is reserved, so nothing shifts when it fills
  };

  return (
    <AccountShell section="upgrade">
      <div className="up-root">

        {/* Hero */}
        <header className="up-hero">
          <div className="up-badge">
            <span className="up-badge-dot" aria-hidden="true" />
            {t('account:upgrade.badge')}
          </div>
          <h1 className="up-title">
            <Trans i18nKey="account:upgrade.title" components={{ em: <em />, br: <br /> }} />
          </h1>
          <p className="up-subtitle">
            {t('account:upgrade.subtitle')}
          </p>
          {/* The price the email promised, above the fold on a phone. One line, not a banner. */}
          {yearlyOffer && (
            <p className="up-hero-offer">
              {t('account:upgrade.offer.heroLine', { price: yearlyOffer.firstYear })}
            </p>
          )}
        </header>

        {/* Free vs Pro comparison */}
        <div className="up-comparison" role="region" aria-label={t('account:upgrade.comparison_aria')}>
          {/* Free column */}
          <div className="up-tier">
            <div className="up-tier-header">
              <span className="up-tier-label">{t('account:upgrade.free.tierLabel')}</span>
              <span className="up-tier-name">{t('account:upgrade.free.tierName')}</span>
              <span className="up-tier-free-label">{t('account:upgrade.free.freeLabel')}</span>
            </div>
            <ul className="up-feature-list" aria-label={t('account:upgrade.free.list_aria')}>
              {FREE_FEATURE_KEYS.map((k) => (
                <li key={k} className="up-feature-item">
                  <span className="up-feature-icon up-feature-icon--check">
                    <CheckIcon />
                  </span>
                  {t(`account:upgrade.freeFeatures.${k}`)}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro column */}
          <div className="up-tier up-tier--pro" aria-label={t('account:upgrade.pro.list_aria')}>
            <div className="up-tier-header">
              <span className="up-tier-label">{t('account:upgrade.pro.tierLabel')}</span>
              <span className="up-tier-name">{t('account:upgrade.pro.tierName')}</span>
              <div className="up-tier-price">
                <span className="up-tier-price-amount">{t('account:upgrade.plans.monthly.price')}</span>
                <span className="up-tier-price-period">{t('account:upgrade.pro.pricePeriod')}</span>
              </div>
              <span className="up-tier-price-alt">{t('account:upgrade.pro.priceAlt')}</span>
            </div>
            <ul className="up-feature-list" aria-label={t('account:upgrade.pro.list_aria')}>
              {FREE_FEATURE_KEYS.map((k) => (
                <li key={k} className="up-feature-item">
                  <span className="up-feature-icon up-feature-icon--check">
                    <CheckIcon />
                  </span>
                  {t(`account:upgrade.freeFeatures.${k}`)}
                </li>
              ))}
              {PRO_FEATURE_KEYS.map((k) => (
                <li key={k} className="up-feature-item up-feature-item--pro">
                  <span className="up-feature-icon up-feature-icon--check">
                    <CheckIcon />
                  </span>
                  {t(`account:upgrade.proFeatures.${k}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="up-error" role="alert">
            <ErrorIcon />
            {error}
          </div>
        )}

        {/* Pricing + CTA (or, for an account that already has Pro, nothing to buy) */}
        {pageState === 'alreadyPro' ? (
          <div className="up-pricing-card">
            <div className="up-already-pro">
              <h2 className="up-already-pro-title" ref={alreadyProTitleRef} tabIndex={-1}>
                {t('account:upgrade.alreadyPro.title')}
              </h2>
              <p className="up-already-pro-body">
                {t(appleManaged ? 'account:upgrade.alreadyPro.bodyApple' : 'account:upgrade.alreadyPro.bodyStripe', { account: accountLabel })}
              </p>
              <div className="up-already-pro-actions">
                {/* The Stripe portal cannot manage an App Store subscription. */}
                {!appleManaged && (
                  <a className="up-cta-btn up-cta-btn--secondary" href="/billing">
                    {t('account:upgrade.alreadyPro.manage')}
                  </a>
                )}
                <button type="button" className="up-text-btn" onClick={switchAccount} disabled={busy}>
                  {t('account:upgrade.switchAccount')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="up-pricing-card">
            <div className="up-pricing-left">
              <p className="up-pricing-title">{t('account:upgrade.pricingTitle')}</p>
              <p className="up-pricing-desc">
                {t('account:upgrade.pricingDesc')}
              </p>
              <div className="up-plan-options" role="radiogroup" aria-label={t('account:upgrade.billingPeriod_aria')}>
                {PLAN_KEYS.map((key) => {
                  const offer = key === 'yearly' ? yearlyOffer : null;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={plan === key}
                      className={`up-plan-option${plan === key ? ' up-plan-option--active' : ''}`}
                      onClick={() => selectPlan(key)}
                    >
                      <span className="up-plan-option-label">{t(`account:upgrade.plans.${key}.label`)}</span>
                      <span className="up-plan-option-price">
                        {offer
                          ? `${offer.firstYear} ${t('account:upgrade.offer.periodFirstYear')}`
                          : `${t(`account:upgrade.plans.${key}.price`)} ${t(`account:upgrade.plans.${key}.period`)}`}
                      </span>
                      <span className="up-plan-option-note">
                        {offer
                          ? t('account:upgrade.offer.thenShort', { price: yearlyPrice })
                          : t(`account:upgrade.plans.${key}.note`)}
                      </span>
                      {/* "Save 33%" compares against monthly; next to an offer price it
                          muddles which number is the deal. */}
                      {PLANS_WITH_BADGE[key] && !offer && (
                        <span className="up-plan-option-badge">{t(`account:upgrade.plans.${key}.badge`)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="up-pricing-right">
              <div className="up-pricing-amount">
                <span className="up-pricing-amount-num">
                  {liveOffer ? liveOffer.firstYear : t(`account:upgrade.plans.${plan}.price`)}
                </span>
                <span className="up-pricing-amount-period">
                  {liveOffer ? t('account:upgrade.offer.periodFirstYear') : t(`account:upgrade.plans.${plan}.period`)}
                </span>
              </div>
              {renderPricingNotes()}
              <div className="up-cta-group">
                <button
                  ref={ctaRef}
                  type="button"
                  className="up-cta-btn"
                  onClick={onCtaPress}
                  disabled={busy}
                  aria-busy={busy}
                >
                  {ctaLabel}
                </button>
                <p className="up-account-line">{renderAccountLine()}</p>
              </div>
            </div>
          </div>
        )}

        {/* Reassurance */}
        <div className="up-reassurance" role="list" aria-label={t('account:upgrade.trustSignals_aria')}>
          <span className="up-reassurance-item" role="listitem">
            <LockIcon />
            {t('account:upgrade.reassurance.secureCheckout')}
          </span>
          <span className="up-reassurance-item" role="listitem">
            <RefreshIcon />
            {t('account:upgrade.reassurance.cancelAnytime')}
          </span>
          <span className="up-reassurance-item" role="listitem">
            <SupportIcon />
            {t('account:upgrade.reassurance.accessUntilEnd')}
          </span>
        </div>

      </div>

      {/* The canonical full-screen sign-in surface (own photo background), not a
          modal: no extra scrim, so the rgba(18,18,24,0.93) modal rule doesn't apply. */}
      {layerOpen && createPortal(
        <div
          className="up-signin-layer"
          role="dialog"
          aria-modal="true"
          aria-label={t('account:upgrade.signIn.dialog_aria')}
        >
          <SignInScreen
            contextLine={liveOffer
              ? t('account:upgrade.signIn.contextOffer', { price: liveOffer.firstYear })
              : t('account:upgrade.signIn.context')}
            onSignedIn={completeSignIn}
            onDismiss={handleDismiss}
          />
        </div>,
        document.body,
      )}
    </AccountShell>
  );
};

export default UpgradePage;
